import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    User, Mail, Phone, MapPin, ArrowLeft, ShieldCheck, FolderOpen,
    Loader2, AlertTriangle, Inbox, ExternalLink,
} from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import CustomerInvitePanel from "@/components/operations/CustomerInvitePanel";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * OPS-CUST-001 · Operations → Customers detail view.
 *
 * Read-only. Displays canonical customer + linked-project rollups,
 * with three navigation-only actions:
 *   * Open project (existing canonical Ops route)
 *   * Manage account (existing Security Administration workspace,
 *     with a minimal `?user=<id>` deep-link parameter — see
 *     AdminSecurity.jsx).
 *   * Back to Customers (restores the list's search/filter state via
 *     the URL params it carried in on).
 */
export default function OperationsCustomerDetail() {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const role = (user?.role || "").toLowerCase();
    const listReturnQuery = location.search || "";

    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const data = await api.opsGetCustomer(customerId);
            setCustomer(data);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                toast.error("That customer no longer exists or is inaccessible.");
                navigate(`/operations/customers${listReturnQuery}`, { replace: true });
                return;
            }
            const msg = formatApiError(e?.response?.data?.detail) || "Failed to load customer";
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [customerId, navigate, listReturnQuery]);

    useEffect(() => { load(); }, [load]);

    const backHref = `/operations/customers${listReturnQuery}`;
    // DEV-020 UAT correction 1 · "Open project" from Customer Detail must
    // navigate to the DEV-020 Single Project Focus route — never the
    // legacy master-detail split (`/operations/projects?project=<id>`).
    // Role-aware: Sales users land on `/sales/projects/:id`, Operations
    // on `/operations/projects/:id`. `returnTo` carries the full current
    // URL (path + `?...`) so Back restores this Customer Detail exactly.
    const focusBase = role === "sales" ? "/sales/projects" : "/operations/projects";
    const returnTo = `${location.pathname}${location.search}`;
    const buildProjectFocusHref = (projectId) =>
        `${focusBase}/${encodeURIComponent(projectId)}`;
    const securityAdminHref = customer
        ? `/settings/security?user=${encodeURIComponent(customer.id)}`
        : "/settings/security";

    return (
        <OperationsShell>
            <PageContainer width="full" testid="ops-customer-detail-page">
                {/* Header */}
                <div className="mb-4">
                    <Link
                        to={backHref}
                        data-testid="ops-customer-back"
                        className="inline-flex items-center gap-1.5 text-xs text-[#4B5565] hover:text-[#131B2F]"
                    >
                        <ArrowLeft size={12} /> Back to Customers
                    </Link>
                </div>
                <PageHeader
                    eyebrow="OPERATIONS"
                    title={customer?.name || (loading ? "Loading…" : "Customer")}
                    subtitle={customer?.email || ""}
                    icon={User}
                    backFallback={backHref}
                    testid="ops-customer-detail"
                />

                {loading && (
                    <div className="p-10 flex items-center justify-center text-sm text-[#4B5565]" data-testid="ops-customer-loading">
                        <Loader2 className="animate-spin mr-2" size={16} /> Loading customer…
                    </div>
                )}

                {!loading && err && (
                    <div className="p-10 text-center rounded-xl border border-[#E5E7EB] bg-white" data-testid="ops-customer-error">
                        <AlertTriangle size={22} className="mx-auto text-[#B91C1C]" />
                        <div className="mt-3 text-sm text-[#131B2F]">{err}</div>
                        <button
                            onClick={load}
                            className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                            data-testid="ops-customer-retry"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {!loading && !err && customer && (
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
                        {/* Column 1 · Identity + projects */}
                        <div className="space-y-4">
                            <section
                                className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5"
                                data-testid="ops-customer-identity"
                            >
                                <div className="text-[11px] uppercase tracking-wide text-[#4B5565]">Contact</div>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2 text-sm text-[#131B2F]">
                                        <Mail size={14} className="text-[#4B5565]" />
                                        <span data-testid="ops-customer-email">{customer.email || "—"}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-[#131B2F]">
                                        <Phone size={14} className="text-[#4B5565]" />
                                        <span data-testid="ops-customer-phone">{customer.mobile || "—"}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-[#131B2F] md:col-span-2">
                                        <MapPin size={14} className="text-[#4B5565]" />
                                        <span data-testid="ops-customer-address">{customer.primary_address || "No installation address on file"}</span>
                                    </div>
                                </div>
                            </section>

                            <CustomerInvitePanel
                                customer={customer}
                                projects={customer.projects || []}
                            />

                            <section
                                className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5"
                                data-testid="ops-customer-projects"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-[11px] uppercase tracking-wide text-[#4B5565]">Linked projects</div>
                                    <div className="text-xs text-[#131B2F] font-medium">
                                        {customer.linked_project_count} <span className="text-[#4B5565] font-normal">({customer.active_project_count} active)</span>
                                    </div>
                                </div>
                                {(!customer.projects || customer.projects.length === 0) && (
                                    <div className="mt-4 py-8 text-center" data-testid="ops-customer-projects-empty">
                                        <Inbox size={20} className="mx-auto text-[#9CA3AF]" />
                                        <div className="mt-2 text-xs text-[#4B5565]">This customer has no linked projects yet.</div>
                                    </div>
                                )}
                                {customer.projects && customer.projects.length > 0 && (
                                    <ul className="mt-3 divide-y divide-[#EDEFF3]">
                                        {customer.projects.map((p) => (
                                            <li key={p.id} data-testid={`ops-customer-project-${p.id}`} className="py-3">
                                                <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-sm text-[#131B2F]">{p.reference}</span>
                                                            {p.state === "completed" && (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5F5F7] text-[#4B5565]">Completed</span>
                                                            )}
                                                            {p.state === "on_hold" && (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">On hold</span>
                                                            )}
                                                            {p.state === "active" && (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EAF6E1] text-[#3E9E2E]">Active</span>
                                                            )}
                                                        </div>
                                                        <div className="mt-0.5 text-xs text-[#4B5565] truncate">
                                                            {p.address || "—"}
                                                        </div>
                                                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#4B5565]">
                                                            {(p.current_stage || p.stage) && (
                                                                <span data-testid={`ops-customer-project-stage-${p.id}`}>
                                                                    Stage: <span className="text-[#131B2F]">{p.stage_label || p.current_stage || p.stage}</span>
                                                                </span>
                                                            )}
                                                            {typeof p.progress === "number" && (
                                                                <span data-testid={`ops-customer-project-progress-${p.id}`}>
                                                                    Progress: <span className="text-[#131B2F]">{p.progress}%</span>
                                                                </span>
                                                            )}
                                                            {p.next_milestone && (
                                                                <span data-testid={`ops-customer-project-next-${p.id}`}>
                                                                    Next: <span className="text-[#131B2F]">{p.next_milestone}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 md:mt-0 flex items-center gap-3">
                                                        <Link
                                                            to={buildProjectFocusHref(p.id)}
                                                            state={{ returnTo }}
                                                            data-testid={`ops-customer-open-project-${p.id}`}
                                                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                                                        >
                                                            <FolderOpen size={12} /> Open project
                                                        </Link>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        </div>

                        {/* Column 2 · Account state + Security Admin deep-link */}
                        <aside className="space-y-4">
                            <section className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5" data-testid="ops-customer-account">
                                <div className="text-[11px] uppercase tracking-wide text-[#4B5565]">Account</div>
                                <div className="mt-2 text-sm text-[#131B2F]">
                                    Status: <span className="font-medium" data-testid="ops-customer-account-status">{customer.account_status || "active"}</span>
                                </div>
                                <div className="mt-1 text-xs text-[#4B5565]">
                                    Portal access: <span data-testid="ops-customer-portal-access">{customer.portal_access || "—"}</span>
                                </div>
                                {customer.last_successful_login_at && (
                                    <div className="mt-1 text-xs text-[#4B5565]">Last login: {customer.last_successful_login_at}</div>
                                )}
                                <Link
                                    to={securityAdminHref}
                                    data-testid="ops-customer-security-admin"
                                    className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#131B2F] text-white text-xs hover:bg-black transition-colors"
                                >
                                    <ShieldCheck size={14} /> Manage account
                                    <ExternalLink size={11} />
                                </Link>
                                <div className="mt-2 text-[11px] text-[#4B5565] leading-relaxed">
                                    Password, permissions, sessions and account status changes are managed in Security Administration — never here.
                                </div>
                            </section>
                        </aside>
                    </div>
                )}
            </PageContainer>
        </OperationsShell>
    );
}
